import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const InvitePopup = ({
  receiverName,
  receiverEmail,
  userEmail,
  onClose,
  onConfirm,
  showCreatePrompt,
}) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedProjectRoles, setSelectedProjectRoles] = useState([]);
  const [roleChoiceType, setRoleChoiceType] = useState(""); // "open" | "custom" | "skip"
  const [selectedOpenRole, setSelectedOpenRole] = useState("");
  const [customRole, setCustomRole] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const username = userEmail?.split("@")[0];

  useEffect(() => {
    if (showCreatePrompt) return;

    const fetchProjects = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/projects/user/${userEmail}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        const enriched = (data.projects || []).map((proj) => {
          const member = (proj.teamMembers || []).find(
            (m) => m.email.toLowerCase() === receiverEmail.toLowerCase()
          );
          return {
            ...proj,
            isMember: !!member,
            memberRole: member?.role || "",
          };
        });

        setProjects(enriched);
      } catch (err) {
        console.error("❌ Failed to fetch projects:", err.message);
      }
    };

    fetchProjects();
  }, [userEmail, receiverEmail, showCreatePrompt]);

  useEffect(() => {
    const matched = projects.find((proj) => proj.id === selectedProjectId);
    const open = matched?.openRoles?.filter((r) => r.status === "open") || [];
    setSelectedProjectRoles(open);
    setRoleChoiceType(""); // reset role state
    setSelectedOpenRole("");
    setCustomRole("");
  }, [selectedProjectId]);

  const handleSend = () => {
    const finalRole =
      roleChoiceType === "open"
        ? selectedOpenRole
        : roleChoiceType === "custom"
        ? customRole
        : null;

    onConfirm(selectedProjectId, finalRole);
    onClose();
  };

  if (showCreatePrompt) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
        <div className="bg-white rounded-xl w-[90%] max-w-md p-6 shadow-lg text-center">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            You need to create a project first
          </h2>
          <p className="text-gray-600 mb-6">
            To invite <span className="font-semibold">{receiverName}</span>, please create a project first.
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => navigate(`/${username}/create`)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Create Project
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl w-[90%] max-w-md p-6 shadow-lg">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          Sending Invite to <span className="text-blue-600">{receiverName}</span>
        </h2>

        {/* Step 1: Select Project */}
        <label className="block text-sm text-gray-600 mb-1">Choose a Project:</label>
        <select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
        >
          <option value="">-- Select your project --</option>
          {projects.map((project) => (
            <option
              key={project.id}
              value={project.isMember ? "" : project.id}
              disabled={project.isMember}
            >
              {project.title}{" "}
              {project.isMember ? `(Already a member: ${project.memberRole})` : ""}
            </option>
          ))}
        </select>

        {/* Step 2: Role Selection Options */}
        {selectedProjectId && (
          <>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Choose Role Option:</label>

              {selectedProjectRoles.length > 0 && (
                <div className="mb-2">
                  <input
                    type="radio"
                    id="openRole"
                    name="roleType"
                    value="open"
                    checked={roleChoiceType === "open"}
                    onChange={() => setRoleChoiceType("open")}
                  />
                  <label htmlFor="openRole" className="ml-2 text-sm text-gray-800">
                    Choose from open roles
                  </label>
                </div>
              )}

              {/* Hidden for now */}
              {/*
              <div className="mb-2">
                <input
                  type="radio"
                  id="customRole"
                  name="roleType"
                  value="custom"
                  checked={roleChoiceType === "custom"}
                  onChange={() => setRoleChoiceType("custom")}
                />
                <label htmlFor="customRole" className="ml-2 text-sm text-gray-800">
                  Define a custom role
                </label>
              </div>
              */}

              <div className="mb-2">
                <input
                  type="radio"
                  id="skipRole"
                  name="roleType"
                  value="skip"
                  checked={roleChoiceType === "skip"}
                  onChange={() => setRoleChoiceType("skip")}
                />
                <label htmlFor="skipRole" className="ml-2 text-sm text-gray-800">
                  Skip role assignment
                </label>
              </div>
            </div>

            {/* Step 3A: Open Role Selector */}
            {roleChoiceType === "open" && (
              <div className="mb-4">
                <select
                  value={selectedOpenRole}
                  onChange={(e) => setSelectedOpenRole(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">-- Select open role --</option>
                  {selectedProjectRoles.map((role, i) => (
                    <option key={i} value={role.id}> 
                      {role.roleTitle}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Step 3B: Custom Role Input - Hidden */}
            {/* 
            {roleChoiceType === "custom" && (
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Enter a special role"
                  value={customRole}
                  onChange={(e) => setCustomRole(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            )} 
            */}
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={
              !selectedProjectId ||
              (roleChoiceType === "open" && !selectedOpenRole) ||
              (roleChoiceType === "custom" && !customRole)
            }
            className={`px-4 py-2 text-sm rounded text-white ${
              selectedProjectId ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-300 cursor-not-allowed"
            }`}
          >
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvitePopup;
